import React from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useLoginForm } from "../../hooks/useLoginForm";
import { usePostPurchase } from "../../hooks/usePostPurchase";
import { ROUTES } from "../../helpers/constants";
import PGLogo from "../../components/logo/PGLogo";
import LoginInput from "./LoginInput";
import Sidebar from "./Sidebar";
import PostPurchaseScreen from "./PostPurchaseScreen";
import "./LoginPage.css";

const LoginPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const emailParam = searchParams.get("email") || "";
    const postPurchase = searchParams.get("postPurchase") === "true";
    const redirectTo = searchParams.get("redirect_to") || "";

    const { isProcessing } = usePostPurchase(emailParam, postPurchase);
    const form = useLoginForm(emailParam, redirectTo);

    if (isProcessing) {
        return <PostPurchaseScreen />;
    }

    return (
        <div className="pg-login-screen">
            <div className="pg-login-screen__content">
                <Sidebar />
                <div className="pg-login-screen__main">
                    <div className="pg-login-screen__form-wrapper">
                        <div className="pg-login-screen__mobile-logo">
                            <PGLogo color="#000000" href={ROUTES.HOME} width={300} height={17} />
                        </div>

                        <Typography
                            component="h1"
                            fontWeight={700}
                            mb="40px"
                            sx={{
                                fontFamily: "'Repro', sans-serif",
                                textTransform: "uppercase",
                                fontSize: "32px",
                                lineHeight: 1.2,
                            }}
                        >
                            Login
                        </Typography>

                        <form onSubmit={form.handleSubmit} noValidate>
                            {form.error && (
                                <div
                                    className="pg-login-screen__form-error"
                                    dangerouslySetInnerHTML={{ __html: form.error }}
                                />
                            )}

                            <LoginInput
                                type="text"
                                name="username"
                                label="Username / Email"
                                value={form.username}
                                onChange={form.handleEmailChange}
                                autoComplete="username"
                                required
                                className="mb-16"
                                validationState={form.emailValid === true ? "valid" : form.emailValid === false ? "invalid" : null}
                            />

                            <LoginInput
                                type="password"
                                name="password"
                                label="Password"
                                value={form.password}
                                onChange={form.handlePasswordChange}
                                required
                                className="mb-16"
                                validationState={form.error && form.password.length > 0 ? "invalid" : null}
                            />

                            <Box display="flex" justifyContent="space-between" alignItems="center" mb="16px">
                                <a href={ROUTES.FORGOT_PASSWORD} className="pg-login-screen__link" style={{ textDecoration: "none" }}>
                                    Forgot password?
                                </a>
                            </Box>

                            <Box display="flex" justifyContent="space-between" alignItems="center" mb="24px">
                                <span className="pg-login-screen__toggle-text">
                                    Remember sign in details
                                </span>
                                <label className="pg-login-screen__switch">
                                    <input
                                        type="checkbox"
                                        checked={form.remember}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setRemember(e.target.checked)}
                                    />
                                    <span className="pg-login-screen__slider pg-login-screen__slider--round" />
                                </label>
                            </Box>

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={!form.isFormValid || form.loading}
                                sx={{
                                    borderRadius: "48px",
                                    padding: "16px",
                                    height: "51px",
                                    fontWeight: 500,
                                    fontFamily: "'Work Sans', sans-serif",
                                    backgroundColor: "#FD3300",
                                    color: "#FFFFFF",
                                    textTransform: "none",
                                    fontSize: "16px",
                                    mb: "16px",
                                    "&:hover": { backgroundColor: "#DB2C00" },
                                    "&:disabled": { backgroundColor: "#DADDDE !important", color: "#898D8F !important" },
                                }}
                            >
                                {form.loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Login"}
                            </Button>
                        </form>

                        <Typography variant="body2" textAlign="center" mb="40px" sx={{ color: "#898D8F", fontSize: "14px", fontFamily: "'Repro', sans-serif" }}>
                            Need help with login?{" "}
                            <a href={ROUTES.CONTACT_EXTERNAL} target="_blank" rel="noreferrer" className="pg-login-screen__link" style={{ textDecoration: "underline" }}>
                                Contact Us
                            </a>
                        </Typography>

                        <Box textAlign="center">
                            <Typography component="h2" fontWeight={600} mb="8px" sx={{ fontFamily: "'Repro', sans-serif", textTransform: "uppercase", fontSize: "20px", textAlign: "center" }}>
                                Don&apos;t have an account?
                            </Typography>
                            <a href={`${ROUTES.CREATE_ACCOUNT}?Welcome`} className="pg-login-screen__link" style={{ textDecoration: "underline", display: "block", width: "100%" }}>
                                Create an account
                            </a>
                        </Box>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
